// Haunted House: a small top-down adventure. Keys, torches, a crate puzzle, polite ghosts,
// and question doors that pay a few gems. Nothing is ever lost; a ghost just sends you back.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import { pick, shuffle } from '@kit/rng';
import { mapFromStrings, moveBody, drawMap, makePad, setTile, tileAt, type Body, type TileMap } from '@kit/tilemap';
import { drawSprite, type Facing } from '@avatar/render';
import type { GameModule, GameContext } from '@shell/types';
import { ROOMS, ROOM_BY_ID, LEGEND, SOLID, TILE, START_ROOM, START_POS, type Room } from './rooms';
import { QUIZ } from '@content/science/quiz';
import { CLUE_WORDS } from '@content/words/clues';

const T = 24, COLS = 17, ROWS = 11;
const SPEED = 88;

interface Ghost { path: [number, number][]; speed: number; seg: number; t: number; x: number; y: number }

interface Question { q: string; choices: string[]; answer: number; why: string }

function mount(host: HTMLElement, ctx: GameContext) {
  const maps: Record<string, TileMap> = {};
  for (const r of ROOMS) maps[r.id] = mapFromStrings(r.rows, LEGEND, T, SOLID);
  const lit = new Set<string>();
  let roomId = START_ROOM;
  let room: Room = ROOM_BY_ID[roomId];
  let map = maps[roomId];
  const body: Body = { x: START_POS[0] * T + 5, y: START_POS[1] * T + 8, w: 14, h: 14, vx: 0, vy: 0 };
  let entry = { x: body.x, y: body.y };
  let facing: Facing = 'down';
  let walkT = 0;
  let keys = 0;
  let ghosts: Ghost[] = [];
  let doorsTried = 0, doorsOpened = 0;
  let finished = false;
  let paused = false;
  let slide = 0;            // room change slide timer
  let slideDir: [number, number] = [0, 0];
  let msgT = 0;
  const t0 = performance.now();
  const used = new Set<string>();   // questions already asked

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('strong', null, room.name), h('span.muted.small', null, 'Keys: ', h('strong', null, '0')));
  const stage = h('div');
  const panel = h('div.stack', { style: { display: 'none' } });
  host.append(head, stage, panel);
  const S = makeSurface(stage, { height: ROWS * T, draw: false });
  const pad = makePad(host, { vertical: true, action: 'Use' });
  let useLatch = false;

  function enterRoom(id: string, side: 'left' | 'right' | 'up' | 'down') {
    roomId = id; room = ROOM_BY_ID[id]; map = maps[id];
    (head.firstElementChild as HTMLElement).textContent = room.name;
    // find the door on the opposite edge and place her just inside it
    const opp = side === 'left' ? 'right' : side === 'right' ? 'left' : side === 'up' ? 'down' : 'up';
    let dx = 8, dy = 5;
    for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
      const id2 = tileAt(map, x, y);
      const onEdge = (opp === 'left' && x === 0) || (opp === 'right' && x === COLS - 1) || (opp === 'up' && y === 0) || (opp === 'down' && y === ROWS - 1);
      if (onEdge && (id2 === TILE.DOOR || id2 === TILE.QDOOR)) { dx = x; dy = y; }
    }
    if (opp === 'left') dx += 1; if (opp === 'right') dx -= 1; if (opp === 'up') dy += 1; if (opp === 'down') dy -= 1;
    body.x = dx * T + 5; body.y = dy * T + 5; body.vx = 0; body.vy = 0;
    entry = { x: body.x, y: body.y };
    ghosts = (room.ghosts || []).map(g => ({ ...g, seg: 0, t: 0, x: g.path[0][0] * T + T / 2, y: g.path[0][1] * T + T / 2 }));
    slide = 0.35; slideDir = side === 'left' ? [-1, 0] : side === 'right' ? [1, 0] : side === 'up' ? [0, -1] : [0, 1];
    if (room.dark && !lit.has(roomId)) ctx.say('It is dark in here. Find the torch and press Use.');
    else ctx.say('');
  }

  function facingTile(): [number, number] {
    const cx = body.x + body.w / 2, cy = body.y + body.h / 2;
    const dx = facing === 'left' ? -1 : facing === 'right' ? 1 : 0, dy = facing === 'up' ? -1 : facing === 'down' ? 1 : 0;
    return [Math.floor((cx + dx * T * 0.75) / T), Math.floor((cy + dy * T * 0.75) / T)];
  }

  function use() {
    const [tx, ty] = facingTile();
    const id = tileAt(map, tx, ty);
    if (id === TILE.TORCH) { setTile(map, tx, ty, TILE.TORCH_LIT); lit.add(roomId); ctx.sound('chime'); ctx.say('The torch lights the room.'); return; }
    if (id === TILE.LOCKED) {
      if (keys > 0) { keys--; setTile(map, tx, ty, TILE.FLOOR); ctx.sound('ding'); ctx.say('Click. The door unlocks.'); updateKeys(); }
      else { ctx.sound('buzz'); ctx.say('Locked. You need a key.'); }
      return;
    }
    if (id === TILE.QDOOR) { askQuestion(tx, ty); return; }
    if (id === TILE.CHEST) { finish(); return; }
    if (id === TILE.CRATE) { pushCrate(tx, ty); return; }
  }

  function pushCrate(tx: number, ty: number) {
    const dx = facing === 'left' ? -1 : facing === 'right' ? 1 : 0, dy = facing === 'up' ? -1 : facing === 'down' ? 1 : 0;
    const nx = tx + dx, ny = ty + dy;
    const beyond = tileAt(map, nx, ny);
    if (beyond === TILE.FLOOR || beyond === TILE.RUG || beyond === TILE.SWITCH) {
      setTile(map, tx, ty, TILE.FLOOR);
      setTile(map, nx, ny, TILE.CRATE);
      ctx.sound('pop');
      if (beyond === TILE.SWITCH) {
        for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) if (tileAt(map, x, y) === TILE.BARRIER) setTile(map, x, y, TILE.FLOOR);
        ctx.sound('chime'); ctx.say('Clunk. The crate holds the switch down and a wall slides away.');
      } else ctx.say('The crate slides.');
    } else ctx.say('The crate will not budge that way.');
  }

  function updateKeys() { (head.querySelector('strong:last-child') as HTMLElement).textContent = String(keys); }

  function nextQuestion(): Question {
    const quiz = QUIZ.filter(q => q.level <= 2 && !used.has(q.id));
    const clues = CLUE_WORDS.filter(w => w.tier === 1 && !used.has('clue:' + w.word));
    if (quiz.length && (ctx.rng() < 0.6 || !clues.length)) {
      const q = pick(ctx.rng, quiz); used.add(q.id);
      // content lists the right answer first; shuffle so the door is a real question
      const order = shuffle(ctx.rng, [0, 1, 2, 3]);
      return { q: q.q, choices: order.map(i => q.choices[i]), answer: order.indexOf(q.answer), why: q.why };
    }
    const w = pick(ctx.rng, clues); used.add('clue:' + w.word);
    const others = shuffle(ctx.rng, CLUE_WORDS.filter(x => x.tier === 1 && x.word !== w.word)).slice(0, 3).map(x => x.word);
    const choices = shuffle(ctx.rng, [w.word, ...others]);
    return { q: `${w.clues[0]} ${w.clues[1]} What is it?`, choices, answer: choices.indexOf(w.word), why: w.clues[4] };
  }

  function askQuestion(tx: number, ty: number) {
    paused = true;
    doorsTried++;
    const q = nextQuestion();
    clear(panel);
    panel.style.display = '';
    panel.append(
      h('div.card', null, h('p.muted.small', null, 'The door whispers a question.'), h('h3', null, q.q)),
      h('div.grid2', null, ...q.choices.map((c, i) => h('button.btn', { onclick: () => answer(i) }, c))),
      h('button.btn.small.ghost.block', { onclick: closePanel }, 'Walk away'),
    );
    function answer(i: number) {
      if (i === q.answer) {
        doorsOpened++;
        setTile(map, tx, ty, TILE.FLOOR);
        ctx.sound('chime');
        ctx.award(10, 'challenge door');
        ctx.say(`Right! ${q.why}`);
        closePanel();
      } else {
        ctx.sound('buzz');
        clear(panel);
        panel.append(
          h('div.card', null, h('p', null, `Not quite. ${q.why}`)),
          h('div.grid2', null,
            h('button.btn.primary', { onclick: () => askQuestion(tx, ty) }, 'Another question'),
            h('button.btn', { onclick: closePanel }, 'Walk away'),
          ),
        );
      }
    }
  }
  function closePanel() { panel.style.display = 'none'; clear(panel); paused = false; }

  function finish() {
    if (finished) return;
    finished = true;
    ctx.sound('chime');
    ctx.say('Treasure! The house is not so scary after all.');
    ctx.charm('charm.ghost');
    setTimeout(() => ctx.end({ attempts: doorsTried, correct: doorsOpened, perfect: doorsTried > 0 && doorsTried === doorsOpened, durationMs: performance.now() - t0, notes: [`Question doors opened: ${doorsOpened}`] }), 900);
  }

  function update(dt: number) {
    if (finished) return;
    if (slide > 0) { slide -= dt; return; }
    if (msgT > 0) msgT -= dt;
    if (paused) { useLatch = pad.action; return; }
    const ix = (pad.right ? 1 : 0) - (pad.left ? 1 : 0), iy = (pad.down ? 1 : 0) - (pad.up ? 1 : 0);
    body.vx = ix * SPEED; body.vy = iy * SPEED;
    if (ix || iy) { facing = Math.abs(ix) >= Math.abs(iy) ? (ix > 0 ? 'right' : 'left') : (iy > 0 ? 'down' : 'up'); walkT += dt * 8; } else walkT = 0;
    const before = { x: body.x, y: body.y };
    moveBody(map, body, dt);
    // bumping a crate while walking pushes it
    if ((ix || iy) && body.x === before.x && body.y === before.y) {
      const [tx, ty] = facingTile();
      if (tileAt(map, tx, ty) === TILE.CRATE) pushCrate(tx, ty);
    }
    if (pad.action && !useLatch) use();
    useLatch = pad.action;
    // pickups
    const cx = Math.floor((body.x + body.w / 2) / T), cy = Math.floor((body.y + body.h / 2) / T);
    const here = tileAt(map, cx, cy);
    if (here === TILE.KEY) { setTile(map, cx, cy, TILE.FLOOR); keys++; updateKeys(); ctx.sound('ding'); ctx.say('A key! It will open a locked door.'); }
    if (here === TILE.DOOR) {
      const side = cx === 0 ? 'left' : cx === COLS - 1 ? 'right' : cy === 0 ? 'up' : 'down';
      const target = (room.exits as any)[side];
      if (target) enterRoom(target, side);
    }
    // ghosts
    for (const g of ghosts) {
      const a = g.path[g.seg], b = g.path[(g.seg + 1) % g.path.length];
      const ax = a[0] * T + T / 2, ay = a[1] * T + T / 2, bx = b[0] * T + T / 2, by = b[1] * T + T / 2;
      const len = Math.hypot(bx - ax, by - ay) || 1;
      g.t += (g.speed * T * dt) / len;
      if (g.t >= 1) { g.t = 0; g.seg = (g.seg + 1) % g.path.length; }
      g.x = ax + (bx - ax) * g.t; g.y = ay + (by - ay) * g.t;
      if (Math.hypot(g.x - (body.x + body.w / 2), g.y - (body.y + body.h / 2)) < 15) {
        body.x = entry.x; body.y = entry.y; body.vx = 0; body.vy = 0;
        ctx.sound('buzz'); ctx.say('Boo! Try again.'); msgT = 1.2;
      }
    }
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    const ox = Math.round((W - COLS * T) / 2);
    c.fillStyle = '#1b1b24'; c.fillRect(0, 0, W, H);
    c.save();
    if (slide > 0) c.translate(slideDir[0] * -slide * 300, slideDir[1] * -slide * 300);
    c.translate(ox, 0);
    const cam = { x: 0, y: 0, w: COLS * T, h: ROWS * T };
    drawMap(c, map, cam, (id, sx, sy, size) => {
      c.fillStyle = '#5a4a5c'; c.fillRect(sx, sy, size, size);
      if (id === TILE.WALL) { c.fillStyle = '#2f2636'; c.fillRect(sx, sy, size, size); c.fillStyle = '#3d3146'; c.fillRect(sx + 2, sy + 2, size - 4, size / 2 - 3); }
      else if (id === TILE.RUG) { c.fillStyle = '#7a3b4e'; c.fillRect(sx + 1, sy + 1, size - 2, size - 2); }
      else if (id === TILE.DOOR) { c.fillStyle = '#8a5a3b'; c.fillRect(sx, sy, size, size); c.fillStyle = '#f5c542'; c.fillRect(sx + size / 2 - 2, sy + size / 2 - 2, 4, 4); }
      else if (id === TILE.LOCKED) { c.fillStyle = '#6b4a2b'; c.fillRect(sx, sy, size, size); c.fillStyle = '#f5c542'; c.fillRect(sx + 8, sy + 9, 8, 7); c.fillRect(sx + 10, sy + 5, 4, 5); }
      else if (id === TILE.KEY) { c.fillStyle = '#f5c542'; c.beginPath(); c.arc(sx + 9, sy + 12, 5, 0, 7); c.fill(); c.fillRect(sx + 12, sy + 10, 9, 3); c.fillRect(sx + 17, sy + 13, 3, 4); }
      else if (id === TILE.TORCH || id === TILE.TORCH_LIT) { c.fillStyle = '#8a5a3b'; c.fillRect(sx + 10, sy + 10, 4, 12); c.fillStyle = id === TILE.TORCH_LIT ? '#f5c542' : '#6a6a72'; c.beginPath(); c.arc(sx + 12, sy + 8, 5, 0, 7); c.fill(); if (id === TILE.TORCH_LIT) { c.fillStyle = '#f08a3c'; c.beginPath(); c.arc(sx + 12, sy + 6, 3, 0, 7); c.fill(); } }
      else if (id === TILE.CRATE) { c.fillStyle = '#b48a5a'; c.fillRect(sx + 2, sy + 2, size - 4, size - 4); c.strokeStyle = '#8a5a3b'; c.lineWidth = 2; c.strokeRect(sx + 4, sy + 4, size - 8, size - 8); }
      else if (id === TILE.SWITCH) { c.fillStyle = '#9a9aa3'; c.fillRect(sx + 5, sy + 5, size - 10, size - 10); c.fillStyle = '#e2574c'; c.fillRect(sx + 9, sy + 9, size - 18, size - 18); }
      else if (id === TILE.BARRIER) { c.fillStyle = '#4f8fe6'; c.fillRect(sx + 3, sy, size - 6, size); c.fillStyle = '#bfe8ff'; c.fillRect(sx + 9, sy + 3, 6, size - 6); }
      else if (id === TILE.QDOOR) { c.fillStyle = '#9b6be6'; c.fillRect(sx, sy, size, size); c.fillStyle = '#fff'; c.font = '900 16px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText('?', sx + size / 2, sy + size / 2 + 1); }
      else if (id === TILE.CHEST) { c.fillStyle = '#8a5a3b'; c.fillRect(sx + 2, sy + 8, size - 4, size - 10); c.fillStyle = '#a0622d'; c.fillRect(sx + 2, sy + 4, size - 4, 6); c.fillStyle = '#f5c542'; c.fillRect(sx + size / 2 - 2, sy + 10, 4, 5); }
    });
    for (const g of ghosts) {
      c.fillStyle = 'rgba(240,240,255,.85)';
      c.beginPath(); c.arc(g.x, g.y - 4, 9, Math.PI, 0); c.lineTo(g.x + 9, g.y + 8); c.lineTo(g.x + 4, g.y + 4); c.lineTo(g.x, g.y + 8); c.lineTo(g.x - 4, g.y + 4); c.lineTo(g.x - 9, g.y + 8); c.closePath(); c.fill();
      c.fillStyle = '#2a2a2e'; c.fillRect(g.x - 5, g.y - 5, 3, 3); c.fillRect(g.x + 2, g.y - 5, 3, 3);
    }
    const frame = walkT > 0 ? Math.floor(walkT) % 4 : 0;
    drawSprite(c, ctx.avatar, body.x + body.w / 2, body.y + body.h + 2, 0.62, frame, facing);
    // darkness with a lantern hole (flat, even-odd)
    if (room.dark && !lit.has(roomId)) {
      c.fillStyle = 'rgba(8,8,16,.93)';
      c.beginPath(); c.rect(-ox, 0, W, H); c.arc(body.x + body.w / 2, body.y, 64, 0, Math.PI * 2, true); c.fill('evenodd');
    }
    c.restore();
  }

  const loop = makeLoop(update, draw);
  loop.start();
  ctx.say('Explore the house. Walk with the arrows, press Use on torches, doors and chests.');
  return { destroy() { loop.stop(); pad.destroy(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'fun.haunted-house',
  title: 'Haunted House',
  blurb: 'Explore five spooky rooms. Find keys, light torches, push crates, dodge polite ghosts.',
  category: 'fun',
  skills: [],
  levelRange: [1, 10],
  unlock: { gems: 250 },
  mount,
};
export default mod;
